import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.syncModelColorOptions();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption = this.updateSelectedOption(event.target);
    const selectedOptionElement = selectedOption instanceof HTMLElement ? selectedOption : event.target;

    this.syncModelColorOptions();

    const selectedVariant = this.selectedVariantForCurrentModelAndColor;
    if (selectedVariant?.featured_media_id) this.selectMediaById(selectedVariant.featured_media_id);

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOptionElement.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOptionElement.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOptionElement), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (selectedVariant?.id) {
      variantId = String(selectedVariant.id);
    } else if (selectedOptionElement instanceof HTMLInputElement && selectedOptionElement.type === 'radio') {
      variantId = selectedOptionElement.dataset.variantId || null;
    } else if (selectedOptionElement instanceof HTMLOptionElement) {
      variantId = selectedOptionElement.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   * @returns {HTMLInputElement | HTMLOptionElement | HTMLSelectElement | undefined}
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
      return target;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
      return newSelectedOption;
    }

    return target instanceof HTMLOptionElement ? target : undefined;
  }

  /** Hides colors that do not exist for the currently selected model. */
  syncModelColorOptions() {
    const modelGroup = this.modelOptionGroup;
    const colorGroup = this.colorOptionGroup;
    const modelPosition = this.getOptionPosition(modelGroup);
    const colorPosition = this.getOptionPosition(colorGroup);
    const selectedModel = this.getSelectedValue(modelGroup);

    if (!modelGroup || !colorGroup || !modelPosition || !colorPosition || !selectedModel || !this.variantIndex.length) {
      return;
    }

    const colorOptions = this.getOptionElements(colorGroup);
    let hasSelectedColor = false;
    let firstExistingColor;

    for (const colorOption of colorOptions) {
      const exists = this.variantIndex.some(
        (variant) => variant[`option${modelPosition}`] === selectedModel && variant[`option${colorPosition}`] === colorOption.value
      );

      colorOption.dataset.optionExists = String(exists);
      colorOption.disabled = !exists;
      colorOption.hidden = !exists;
      this.setOptionHidden(colorOption, !exists);

      if (!exists) {
        colorOption.setAttribute('aria-disabled', 'true');
      } else if (colorOption.dataset.optionAvailable === 'false') {
        colorOption.setAttribute('aria-disabled', 'true');
      } else {
        colorOption.removeAttribute('aria-disabled');
      }

      if (exists && !firstExistingColor) firstExistingColor = colorOption;
      if (exists && this.isOptionSelected(colorOption)) hasSelectedColor = true;
    }

    if (!hasSelectedColor && firstExistingColor) this.selectOptionElement(firstExistingColor);
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement} element
   */
  selectOptionElement(element) {
    if (element instanceof HTMLInputElement) {
      element.checked = true;
      return;
    }

    const select = element.parentElement;
    if (!(select instanceof HTMLSelectElement)) return;

    select.value = element.value;
    for (const option of select.options) {
      option.removeAttribute('selected');
    }
    element.setAttribute('selected', 'selected');
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement} element
   * @returns {boolean}
   */
  isOptionSelected(element) {
    return element instanceof HTMLInputElement ? element.checked : element.selected || element.hasAttribute('selected');
  }

  /**
   * @param {Element | undefined | null} element
   * @returns {number | undefined}
   */
  getOptionPosition(element) {
    const inputId = element instanceof HTMLElement
      ? element.dataset.inputId || element.querySelector('[data-input-id]')?.dataset.inputId
      : undefined;
    if (!inputId) return undefined;

    const position = Number(inputId.split('-')[0]);
    return Number.isInteger(position) ? position : undefined;
  }

  /**
   * @param {Element | undefined | null} group
   * @returns {Array<HTMLInputElement | HTMLOptionElement>}
   */
  getOptionElements(group) {
    if (!group) return [];

    return Array.from(group.querySelectorAll('[data-input-id]')).filter(
      (element) => element instanceof HTMLInputElement || element instanceof HTMLOptionElement
    );
  }

  /**
   * @param {Element | undefined | null} group
   * @returns {string | undefined}
   */
  getSelectedValue(group) {
    return this.getOptionElements(group).find((element) => this.isOptionSelected(element))?.value;
  }

  /**
   * @param {string | number} mediaId
   */
  selectMediaById(mediaId) {
    const gallery = this.closest('.shopify-section')?.querySelector('[id^="ProductMediaGallery-"]');
    gallery?.__selectMediaById?.(mediaId);
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement} option
   * @param {boolean} hidden
   */
  setOptionHidden(option, hidden) {
    if (option instanceof HTMLOptionElement) {
      option.hidden = hidden;
      return;
    }

    const label = option.closest('label');
    if (!label) return;

    label.toggleAttribute('hidden', hidden);
    label.style.display = hidden ? 'none' : '';
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.syncModelColorOptions();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Gets the small variant index used for model/color option filtering.
   * @returns {Array<Record<string, string | number | boolean | null>>}
   */
  get variantIndex() {
    const textContent = this.querySelector('script[data-variant-picker-variants]')?.textContent;
    if (!textContent) return [];

    try {
      const variants = JSON.parse(textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (_error) {
      return [];
    }
  }

  get modelOptionGroup() {
    return this.querySelector('.variant-option--model');
  }

  get colorOptionGroup() {
    return this.querySelector('.variant-option--color');
  }

  /**
   * Gets the variant represented by the current model/color selection.
   */
  get selectedVariantForCurrentModelAndColor() {
    const modelGroup = this.modelOptionGroup;
    const colorGroup = this.colorOptionGroup;
    const modelPosition = this.getOptionPosition(modelGroup);
    const colorPosition = this.getOptionPosition(colorGroup);
    const selectedModel = this.getSelectedValue(modelGroup);
    const selectedColor = this.getSelectedValue(colorGroup);

    if (!modelPosition || !colorPosition || !selectedModel || !selectedColor) return undefined;

    return this.variantIndex.find(
      (variant) => variant[`option${modelPosition}`] === selectedModel && variant[`option${colorPosition}`] === selectedColor
    );
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
